// ==================== PAYMENT HISTORY PAGE ====================

import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Receipt,
  Download,
  RefreshCw,
  AlertCircle,
  CheckCircle,
  XCircle,
  X,
  Calendar,
  CreditCard,
  FileText,
  Building,
  Mail,
  Phone,
  MapPin,
  Clock,
  Zap,
  ArrowRight,
  Globe,
  Hash,
  Banknote,
} from 'lucide-react';
import { Button, Pagination, Modal } from '../../../components/ui';
import { 
  subscriptionService, 
  type PaymentDetails, 
  type Invoice 
} from '../../../services/subscription.service';
import logoLight from '../../../assets/amplifymsg_logo_light_transparent.png';
import logoDark from '../../../assets/amplifymsg_logo_dark_transparent.png';

// ==================== CARD BRAND ICONS ====================
const cardBrandIcons: Record<string, string> = {
  visa: '💳',
  mastercard: '💳',
  amex: '💳',
  discover: '💳',
  default: '💳',
};

// ==================== INVOICE PREVIEW COMPONENT ====================
interface InvoicePreviewProps {
  invoice: Invoice;
  formatCurrency: (amount: number, currency: string) => string;
  formatDate: (date: string) => string;
}

const InvoicePreview = ({ invoice, formatCurrency, formatDate }: InvoicePreviewProps) => {
  
  const generateInvoiceHTML = () => {
    const companyName = 'Amplify MSG';
    const companyTagline = 'Powered by NKU Technologies (PVT) Ltd';
    const companyWebsite = invoice.company?.website || 'www.amplifymsg.com';
    const companyEmail = invoice.company?.email || 'billing@amplifymsg.com';
    const accent = '#E68E37';
    const dark = '#0D1117';

    return `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Invoice ${invoice.invoiceNumber} - ${companyName}</title>
        <style>
          @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap');
          * { box-sizing: border-box; margin: 0; padding: 0; }
          body { font-family: 'Inter', sans-serif; background: #fff; color: #222; line-height: 1.5; }
          .page { max-width: 700px; margin: 0 auto; position: relative; min-height: 100vh; padding: 0 0.4in; }

          /* ---- Top edge bars ---- */
          .edge-top { display: flex; height: 5px; }
          .edge-top .bar-dark { flex: 1; background: ${dark}; }
          .edge-top .bar-accent { width: 120px; background: ${accent}; }

          /* ---- Header ---- */
          .header { display: flex; justify-content: space-between; align-items: flex-start; padding: 30px 16px 20px; }
          .logo-area {}
          .logo-area img { height: 120px; width: 350px; object-fit: contain; object-position: left; }
          .invoice-title { text-align: right; }
          .invoice-title h1 { font-size: 34px; font-weight: 800; color: ${accent}; letter-spacing: 2px; font-style: italic; }
          .invoice-title .num-label { font-size: 10px; color: #aaa; text-transform: uppercase; letter-spacing: 1px; margin-top: 12px; }
          .invoice-title .num-value { font-size: 24px; font-weight: 700; color: ${dark}; letter-spacing: 1px; }

          /* ---- Company & Bill To ---- */
          .details-section { padding: 0 16px 8px; }
          .company-name { font-size: 17px; font-weight: 800; color: ${dark}; }
          .company-sub { font-size: 11px; color: #666; font-weight: 500; margin-bottom: 2px; }
          
          .bill-to { margin-top: 10px; }
          .bill-to .bt-label { font-size: 13px; font-weight: 700; color: ${dark}; }
          .bill-to .bt-value { font-size: 12px; color: #555; line-height: 1.7; }
          .bill-to .bt-value span { color: #222; }

          /* ---- Table ---- */
          .items-table { width: 100%; border-collapse: collapse; margin-top: 20px; }
          .items-table thead th {
            background: ${dark}; color: #fff; font-size: 10px; text-transform: uppercase;
            letter-spacing: 0.8px; padding: 9px 16px; font-weight: 600; text-align: left;
          }
          .items-table thead th:nth-child(2),
          .items-table thead th:nth-child(3) { text-align: center; }
          .items-table thead th:last-child { text-align: right; }
          .items-table thead th:first-child { border-left: 3px solid ${accent}; }
          .items-table tbody td {
            padding: 11px 16px; font-size: 12px; color: #444;
            border-bottom: 1px solid #eee;
          }
          .items-table tbody td:nth-child(2),
          .items-table tbody td:nth-child(3) { text-align: center; }
          .items-table tbody td:last-child { text-align: right; font-weight: 600; color: ${dark}; }

          /* ---- Summary ---- */
          .summary { display: flex; justify-content: space-between; padding: 28px 16px 0; }
          .pay-info { max-width: 55%; }
          .pay-info p { font-size: 11px; color: #888; margin-bottom: 3px; }
          .pay-info strong { color: #333; font-weight: 600; }
          .totals-box { width: 200px; }
          .totals-row { display: flex; justify-content: space-between; padding: 5px 0; font-size: 12px; color: #666; }
          .totals-row .label { font-weight: 500; }
          .totals-row .val { font-weight: 600; color: #333; }
          .totals-row.grand { border-top: 2px solid ${dark}; margin-top: 6px; padding-top: 8px; }
          .totals-row.grand .label { font-size: 13px; font-weight: 700; color: ${dark}; }
          .totals-row.grand .val { font-size: 16px; font-weight: 800; color: ${dark}; }

          /* ---- Divider ---- */
          .divider { margin: 28px 16px 0; border: none; border-top: 1px solid #ddd; }

          /* ---- Terms ---- */
          .terms { padding: 20px 16px 0; }
          .terms h4 { font-size: 12px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.8px; color: ${dark}; margin-bottom: 8px; }
          .terms p { font-size: 10px; color: #999; line-height: 1.7; }

          /* ---- Footer ---- */
          .footer { margin-top: 30px; }

          .footer-bar {
            height: 3px;
            background: linear-gradient(90deg, ${dark} 0%, ${dark} 40%, ${accent} 40%, ${accent} 60%, ${dark} 60%, ${dark} 100%);
          }
          .footer-main {
            background: ${dark};
            padding: 16px 24px 12px;
          }
          .footer-contacts {
            display: flex;
            justify-content: space-between;
            align-items: flex-start;
            gap: 16px;
          }
          .f-col {
            display: flex;
            align-items: flex-start;
            gap: 10px;
            flex: 1;
          }
          .f-col.center { justify-content: center; text-align: center; }
          .f-col.right { justify-content: flex-end; text-align: right; }
          .f-icon-wrap {
            width: 28px; height: 28px; border-radius: 6px;
            background: rgba(255,255,255,0.08);
            border: 1px solid rgba(255,255,255,0.12);
            display: flex; align-items: center; justify-content: center;
            flex-shrink: 0; margin-top: 1px;
          }
          .f-icon-wrap svg { width: 13px; height: 13px; fill: ${accent}; }
          .f-label {
            font-size: 8px; text-transform: uppercase; letter-spacing: 1.2px;
            color: ${accent}; font-weight: 700; margin-bottom: 3px;
          }
          .f-value { font-size: 10px; color: rgba(255,255,255,0.85); line-height: 1.5; }
          .footer-bottom {
            margin-top: 10px; padding-top: 8px;
            border-top: 1px solid rgba(255,255,255,0.08);
            display: flex; justify-content: space-between; align-items: center;
          }
          .footer-copy { font-size: 8px; color: rgba(255,255,255,0.35); letter-spacing: 0.5px; }
          .footer-brand { font-size: 8px; color: rgba(255,255,255,0.35); letter-spacing: 0.5px; }

          /* Fixed footer that repeats on every printed page */
          .print-footer {
            display: none;
          }

          /* Print layout table trick to reserve footer space on every page */
          .print-layout { width: 100%; }
          .print-layout > thead > tr > td,
          .print-layout > tfoot > tr > td { padding: 0; }
          .footer-spacer { height: 0; }

          /* Fixed footer that repeats on every printed page */
          .print-footer { display: none; }

          @media print {
            body { -webkit-print-color-adjust: exact; print-color-adjust: exact; margin: 0; }
            .page { max-width: 100%; min-height: auto !important; }
            @page { margin: 0.3in 0 0 0; size: A4; }

            .footer { display: none !important; }

            .footer-spacer { height: 80px; }

            .print-footer {
              display: block;
              position: fixed;
              bottom: 0;
              left: 0;
              right: 0;
            }

            .items-table { page-break-inside: auto; }
            .items-table tr { page-break-inside: avoid; page-break-after: auto; }
          }
        </style>
      </head>
      <body>
        <!-- Table layout: tfoot spacer reserves footer height on every printed page -->
        <table class="print-layout"><tfoot><tr><td><div class="footer-spacer"></div></td></tr></tfoot>
        <tbody><tr><td>

        <div class="page">

          <!-- Top edge -->
          <div class="edge-top">
            <div class="bar-dark"></div>
            <div class="bar-accent"></div>
            <div class="bar-dark" style="flex: 0.3;"></div>
          </div>

          <!-- Header -->
          <div class="header">
            <div class="logo-area">
              <img src="${logoLight}" alt="${companyName}" />
            </div>
            <div class="invoice-title">
              <h1>INVOICE</h1>
              <div class="num-label">Invoice#</div>
              <div class="num-value">${invoice.invoiceNumber}</div>
            </div>
          </div>

          <!-- Company & Bill To -->
          <div class="details-section">
            <div class="company-name">${companyName}</div>
            <div class="company-sub">${companyTagline}</div>
            <div class="company-sub" style="color: #222; font-weight: 600; font-style: italic;">Address: 18 Zaman Park, Lane 2, Canal Road, Lahore, Pakistan</div>
            <div class="bill-to">
              <div class="bt-label">Bill To:</div>
              <div class="bt-value">
                ${invoice.customer.name}
                ${invoice.customer.phone && invoice.customer.phone !== 'N/A' ? `<br/>${invoice.customer.phone}` : ''}
                 - <span>${invoice.customer.email}</span>
                ${invoice.customer.address && invoice.customer.address !== 'N/A' ? `<br/>${invoice.customer.address}` : ''}
              </div>
            </div>
          </div>

          <!-- Items Table -->
          <table class="items-table" cellpadding="0" cellspacing="0">
            <thead>
              <tr>
                <th>Products</th>
                <th>Price</th>
                <th>Qty</th>
                <th>Total</th>
              </tr>
            </thead>
            <tbody>
              ${invoice.lineItems.map(item => `
                <tr>
                  <td>${item.description}</td>
                  <td>${formatCurrency(item.amount, invoice.currency)}</td>
                  <td>1</td>
                  <td>${formatCurrency(item.amount, invoice.currency)}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>

          <!-- Summary -->
          <div class="summary">
            <div class="pay-info">
              <p><strong>PAYMENT DATA:</strong></p>
              ${invoice.paymentMethod ? `
                <p><strong>METHOD:</strong> ${invoice.paymentMethod.brand || 'Card'} •••• ${invoice.paymentMethod.last4}</p>
                ${invoice.paymentMethod.expiryMonth && invoice.paymentMethod.expiryYear ? `<p><strong>EXPIRES:</strong> ${invoice.paymentMethod.expiryMonth}/${invoice.paymentMethod.expiryYear}</p>` : ''}
              ` : `<p><strong>PAYMENT METHOD:</strong> ${invoice.paymentMode === 'CASH' ? 'Cash Payment' : invoice.paymentMode === 'CHECK' ? 'Via Cheque' : invoice.paymentMode === 'BANK_TRANSFER' ? 'Via Bank Transfer' : invoice.paymentMode === 'OTHER' ? 'Other' : 'Manual / Offline Payment'}</p>`}
              ${invoice.paymentDescription ? `<p><strong>NOTE:</strong> ${invoice.paymentDescription}</p>` : ''}
              <p><strong>DATE:</strong> ${formatDate(invoice.invoiceDate)}</p>
              ${invoice.paidDate ? `<p><strong>PAID:</strong> ${formatDate(invoice.paidDate)}</p>` : ''}
              <p><strong>STATUS:</strong> ${invoice.status === 'SUCCEEDED' ? '✓ Paid' : invoice.status}</p>
            </div>
            <div class="totals-box">
              <div class="totals-row">
                <span class="label">SUBTOTAL</span>
                <span class="val">${invoice.formatted.subtotal || invoice.formatted.total}</span>
              </div>
              <div class="totals-row grand">
                <span class="label">TOTAL</span>
                <span class="val">${invoice.formatted.total}</span>
              </div>
            </div>
          </div>

          <!-- Divider -->
          <hr class="divider" />

          <!-- Terms -->
          <div class="terms">
            <h4>Terms and Conditions</h4>
            <p>
              This invoice is auto-generated and serves as proof of payment for your ${companyName} subscription.
              All payments are non-refundable unless stated otherwise in our terms of service.
              For billing inquiries, please contact us at ${companyEmail}.
            </p>
          </div>

          <!-- Footer -->
          <div class="footer">
            <div class="footer-bar"></div>
            <div class="footer-main">
              <div class="footer-contacts">
                <div class="f-col">
                  <div class="f-icon-wrap">
                    <svg viewBox="0 0 24 24"><path d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z"/></svg>
                  </div>
                  <div>
                    <div class="f-label">Phone</div>
                    <div class="f-value">+92 300 100 4001</div>
                  </div>
                </div>
                <div class="f-col center">
                  <div class="f-icon-wrap">
                    <svg viewBox="0 0 24 24"><path d="M20 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z"/></svg>
                  </div>
                  <div>
                    <div class="f-label">Email &amp; Web</div>
                    <div class="f-value">${companyEmail}<br/>${companyWebsite}</div>
                  </div>
                </div>
                <div class="f-col right">
                  <div class="f-icon-wrap">
                    <svg viewBox="0 0 24 24"><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5S10.62 6.5 12 6.5s2.5 1.12 2.5 2.5S13.38 11.5 12 11.5z"/></svg>
                  </div>
                  <div>
                    <div class="f-label">Address</div>
                    <div class="f-value">18 Zaman Park, Lane 2,<br/>Canal Road, Lahore, Pakistan</div>
                  </div>
                </div>
              </div>
              <div class="footer-bottom">
                <div class="footer-copy">&copy; ${new Date().getFullYear()} ${companyName}. All rights reserved.</div>
                <div class="footer-brand">Powered by NKU Technologies (PVT) Ltd</div>
              </div>
            </div>
          </div>

        </div>

        </td></tr></tbody></table>

        <!-- Fixed footer for every printed page -->
        <div class="print-footer">
          <div class="footer-bar"></div>
          <div class="footer-main">
            <div class="footer-contacts">
              <div class="f-col">
                <div class="f-icon-wrap">
                  <svg viewBox="0 0 24 24"><path d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z"/></svg>
                </div>
                <div>
                  <div class="f-label">Phone</div>
                  <div class="f-value">+92 300 100 4001</div>
                </div>
              </div>
              <div class="f-col center">
                <div class="f-icon-wrap">
                  <svg viewBox="0 0 24 24"><path d="M20 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z"/></svg>
                </div>
                <div>
                  <div class="f-label">Email &amp; Web</div>
                  <div class="f-value">${companyEmail}<br/>${companyWebsite}</div>
                </div>
              </div>
              <div class="f-col right">
                <div class="f-icon-wrap">
                  <svg viewBox="0 0 24 24"><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5S10.62 6.5 12 6.5s2.5 1.12 2.5 2.5S13.38 11.5 12 11.5z"/></svg>
                </div>
                <div>
                  <div class="f-label">Address</div>
                  <div class="f-value">18 Zaman Park, Lane 2,<br/>Canal Road, Lahore, Pakistan</div>
                </div>
              </div>
            </div>
            <div class="footer-bottom">
              <div class="footer-copy">&copy; ${new Date().getFullYear()} ${companyName}. All rights reserved.</div>
              <div class="footer-brand">Powered by NKU Technologies (PVT) Ltd</div>
            </div>
          </div>
        </div>
      </body>
      </html>
    `;
  };

  const handleDownloadPDF = () => {
    const invoiceHtml = generateInvoiceHTML();
    
    // Create a hidden iframe to generate PDF
    const iframe = document.createElement('iframe');
    iframe.style.position = 'absolute';
    iframe.style.left = '-9999px';
    iframe.style.width = '800px';
    iframe.style.height = '1200px';
    document.body.appendChild(iframe);
    
    const iframeDoc = iframe.contentDocument || iframe.contentWindow?.document;
    if (!iframeDoc) {
      document.body.removeChild(iframe);
      return;
    }
    
    iframeDoc.open();
    iframeDoc.write(invoiceHtml);
    iframeDoc.close();
    
    // Wait for content to load then print to PDF
    setTimeout(() => {
      iframe.contentWindow?.print();
      // Remove iframe after a delay to allow print dialog
      setTimeout(() => {
        document.body.removeChild(iframe);
      }, 1000);
    }, 500);
  };

  const handlePrint = () => {
    const invoiceHtml = generateInvoiceHTML();
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    printWindow.document.write(invoiceHtml);
    printWindow.document.close();
    printWindow.print();
  };

  return (
    <div className="space-y-6">
      {/* Professional Invoice Header */}
      <div className="flex items-start justify-between pb-1 border-b border-gray-800">
        <div>
          <img src={logoDark} alt="AmplifyMsg" className="h-auto w-24" />
        </div>
        <div className="text-right">
          <div className="text-[10px] uppercase tracking-wider text-(--color-muted-gray) mb-0.5">Invoice</div>
          <div className="text-sm font-mono font-semibold text-white">{invoice.invoiceNumber}</div>
          <div className="mt-2">
            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium ${
              invoice.status === 'SUCCEEDED'
                ? 'bg-emerald-500/15 text-emerald-400'
                : 'bg-yellow-500/15 text-yellow-400'
            }`}>
              <CheckCircle className="w-3 h-3" />
              {invoice.status === 'SUCCEEDED' ? 'Paid' : invoice.status}
            </span>
          </div>
        </div>
      </div>

      {/* Customer & Invoice Details Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Bill To */}
        <div className="bg-[#0D1117] rounded-lg p-4 border border-gray-800/50">
          <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-(--color-muted-gray) mb-3">
            <Building className="w-3 h-3" /> Bill To
          </div>
          <h4 className="text-sm font-semibold text-white mb-2">{invoice.customer.name}</h4>
          <div className="space-y-1.5">
            <p className="text-xs text-(--color-muted-gray) flex items-center gap-1.5">
              <Mail className="w-3 h-3 shrink-0" /> 
              <span className="truncate">{invoice.customer.email}</span>
            </p>
            {invoice.customer.phone && invoice.customer.phone !== 'N/A' && (
              <p className="text-xs text-(--color-muted-gray) flex items-center gap-1.5">
                <Phone className="w-3 h-3 shrink-0" /> {invoice.customer.phone}
              </p>
            )}
            {invoice.customer.address && invoice.customer.address !== 'N/A' && (
              <p className="text-xs text-(--color-muted-gray) flex items-center gap-1.5">
                <MapPin className="w-3 h-3 shrink-0" /> {invoice.customer.address}
              </p>
            )}
          </div>
        </div>

        {/* Invoice Details */}
        <div className="bg-[#0D1117] rounded-lg p-4 border border-gray-800/50">
          <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-(--color-muted-gray) mb-3">
            <FileText className="w-3 h-3" /> Invoice Details
          </div>
          <div className="space-y-2">
            <div className="flex justify-between items-center">
              <span className="text-xs text-(--color-muted-gray) flex items-center gap-1.5">
                <Calendar className="w-3 h-3" /> Invoice Date
              </span>
              <span className="text-xs text-white font-medium">{formatDate(invoice.invoiceDate)}</span>
            </div>
            {invoice.paidDate && (
              <div className="flex justify-between items-center">
                <span className="text-xs text-(--color-muted-gray) flex items-center gap-1.5">
                  <CheckCircle className="w-3 h-3" /> Paid Date
                </span>
                <span className="text-xs text-white font-medium">{formatDate(invoice.paidDate)}</span>
              </div>
            )}
            <div className="flex justify-between items-center">
              <span className="text-xs text-(--color-muted-gray) flex items-center gap-1.5">
                <Hash className="w-3 h-3" /> Reference
              </span>
              <span className="text-xs text-white font-mono">{invoice.paymentId.slice(0, 8).toUpperCase()}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Line Items */}
      <div className="bg-[#0D1117] rounded-lg border border-gray-800/50 overflow-hidden">
        <div className="grid grid-cols-[1fr,auto] gap-4 px-4 py-2.5 bg-[#1A2332]/50 border-b border-gray-800">
          <div className="text-[10px] uppercase tracking-wider font-medium text-(--color-muted-gray)">Description</div>
          <div className="text-[10px] uppercase tracking-wider font-medium text-(--color-muted-gray) text-right">Amount</div>
        </div>
        <div className="divide-y divide-gray-800/50">
          {invoice.lineItems.map((item, index) => (
            <div key={index} className="grid grid-cols-[1fr,auto] gap-4 px-4 py-3 items-center">
              <div className="flex items-center gap-2">
                {item.type === 'setup' ? (
                  <div className="w-6 h-6 rounded bg-orange-500/15 flex items-center justify-center">
                    <Zap className="w-3 h-3 text-orange-400" />
                  </div>
                ) : (
                  <div className="w-6 h-6 rounded bg-blue-500/15 flex items-center justify-center">
                    <Receipt className="w-3 h-3 text-blue-400" />
                  </div>
                )}
                <span className="text-sm text-white">{item.description}</span>
              </div>
              <span className="text-sm font-medium text-white font-mono">
                {formatCurrency(item.amount, invoice.currency)}
              </span>
            </div>
          ))}
        </div>
        
        {/* Total */}
        <div className="px-4 py-4 bg-gradient-to-r from-[#1A2332] to-[#1A2332]/50 border-t border-gray-800 flex justify-between items-center">
          <div className="flex items-center gap-2">
            <Banknote className="w-4 h-4 text-orange-400" />
            <span className="text-sm font-semibold text-white">Total Amount</span>
          </div>
          <span className="text-lg font-bold text-orange-400 font-mono">
            {invoice.formatted.total}
          </span>
        </div>
      </div>

      {/* Payment Method */}
      {invoice.paymentMethod && (
        <div className="bg-[#0D1117] rounded-lg p-4 border border-gray-800/50">
          <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-(--color-muted-gray) mb-3">
            <CreditCard className="w-3 h-3" /> Payment Method
          </div>
          <div className="flex items-center gap-3">
            <div className="w-12 h-8 bg-gradient-to-br from-gray-600 to-gray-700 rounded flex items-center justify-center">
              <span className="text-lg">{cardBrandIcons[invoice.paymentMethod.brand?.toLowerCase() || 'default']}</span>
            </div>
            <div>
              <p className="text-sm text-white font-medium capitalize">
                {invoice.paymentMethod.brand || 'Card'} •••• {invoice.paymentMethod.last4}
              </p>
              {invoice.paymentMethod.expiryMonth && invoice.paymentMethod.expiryYear && (
                <p className="text-xs text-(--color-muted-gray)">
                  Expires {invoice.paymentMethod.expiryMonth}/{invoice.paymentMethod.expiryYear}
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="flex items-center justify-between pt-4 border-t border-gray-800">
        <div className="flex items-center gap-2 text-xs text-(--color-muted-gray)">
          <Globe className="w-3.5 h-3.5" />
          <span>{invoice.company?.email || 'billing@amplifymsg.com'}</span>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="secondary" size="sm" onClick={handlePrint}>
            <FileText className="w-3.5 h-3.5 mr-1.5" /> Print
          </Button>
          <Button variant="primary" size="sm" onClick={handleDownloadPDF}>
            <Download className="w-3.5 h-3.5 mr-1.5" /> Download PDF
          </Button>
        </div>
      </div>
    </div>
  );
};

// ==================== MAIN COMPONENT ====================
const PaymentHistoryPage = () => {
  const [payments, setPayments] = useState<PaymentDetails[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [selectedInvoice, setSelectedInvoice] = useState<Invoice | null>(null);
  const [loadingInvoice, setLoadingInvoice] = useState<string | null>(null);

  const fetchPayments = useCallback(async () => {
    try {
      setLoading(true);
      const response = await subscriptionService.getPaymentHistory({ page, limit: pageSize });
      if (response.success && response.data) {
        setPayments(response.data.items);
        setTotalPages(response.data.totalPages);
        setTotalItems(response.data.total);
      } else {
        setError(response.message || 'Failed to load payment history');
      }
    } catch {
      setError('Failed to load payment history');
    } finally {
      setLoading(false);
    }
  }, [page, pageSize]);

  useEffect(() => {
    fetchPayments();
  }, [fetchPayments]);

  const handleViewInvoice = async (paymentId: string) => {
    try {
      setLoadingInvoice(paymentId);
      const response = await subscriptionService.getInvoice(paymentId);
      if (response.success && response.data) {
        setSelectedInvoice(response.data);
      } else {
        setError(response.message || 'Failed to load invoice');
      }
    } catch {
      setError('Failed to load invoice');
    } finally {
      setLoadingInvoice(null);
    }
  };

  const formatCurrency = (amount: number, currency: string = 'USD') => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const formatTime = (dateString: string) => {
    return new Date(dateString).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getStatusConfig = (status: string) => {
    switch (status) {
      case 'SUCCEEDED':
        return { 
          bg: 'bg-emerald-500/15', 
          text: 'text-emerald-400', 
          border: 'border-emerald-500/30',
          icon: CheckCircle,
          label: 'Paid'
        };
      case 'FAILED':
        return { 
          bg: 'bg-red-500/15', 
          text: 'text-red-400',
          border: 'border-red-500/30',
          icon: XCircle,
          label: 'Failed'
        };
      case 'PENDING':
        return { 
          bg: 'bg-yellow-500/15', 
          text: 'text-yellow-400',
          border: 'border-yellow-500/30',
          icon: Clock,
          label: 'Pending'
        };
      default:
        return { 
          bg: 'bg-gray-500/15', 
          text: 'text-gray-400',
          border: 'border-gray-500/30',
          icon: AlertCircle,
          label: status
        };
    }
  };

  const getPaymentTypeLabel = (type: string) => {
    switch (type) {
      case 'SUBSCRIPTION': return 'New Subscription';
      case 'RENEWAL': return 'Renewal';
      case 'UPGRADE': return 'Plan Upgrade';
      case 'MANUAL': return 'Manual Payment';
      default: return type;
    }
  };

  if (loading && payments.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex flex-col items-center gap-3">
          <div className="relative">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-orange-500/20 to-orange-600/10 flex items-center justify-center">
              <Receipt className="w-6 h-6 text-orange-500" />
            </div>
            <RefreshCw className="w-4 h-4 text-orange-500 animate-spin absolute -bottom-1 -right-1" />
          </div>
          <p className="text-sm text-(--color-muted-gray)">Loading payment history...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-base font-heading font-semibold text-white">Payment History</h2>
          <p className="text-xs text-(--color-muted-gray) mt-0.5">
            View your transactions and download invoices
          </p>
        </div>
        {totalItems > 0 && (
          <div className="flex items-center gap-2 text-xs text-(--color-muted-gray) bg-[#1A2332] px-3 py-1.5 rounded-lg">
            <Receipt className="w-3.5 h-3.5" />
            <span>{totalItems} transaction{totalItems !== 1 ? 's' : ''}</span>
          </div>
        )}
      </div>

      {/* Error Alert */}
      <AnimatePresence>
        {error && (
          <motion.div 
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg flex items-center gap-2"
          >
            <AlertCircle className="w-4 h-4 text-red-400 shrink-0" />
            <p className="text-sm text-red-400 flex-1">{error}</p>
            <button onClick={() => setError(null)} className="text-red-400 hover:text-red-300 p-1">
              <X className="w-3.5 h-3.5" />
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Payments List */}
      <div className="bg-[#121723] rounded-xl border border-gray-800 overflow-hidden">
        {payments.length === 0 ? (
          <div className="p-12 text-center">
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-gray-700/30 to-gray-800/30 flex items-center justify-center mx-auto mb-4 border border-gray-700/50">
              <Receipt className="w-8 h-8 text-gray-500" />
            </div>
            <h3 className="text-sm font-heading font-semibold text-white mb-1.5">No Payments Yet</h3>
            <p className="text-xs text-(--color-muted-gray) max-w-xs mx-auto">
              Your payment history will appear here once you make your first payment.
            </p>
          </div>
        ) : (
          <>
            {/* Payment Cards */}
            <div className="divide-y divide-gray-800/50">
              {payments.map((payment, index) => {
                const statusConfig = getStatusConfig(payment.status);
                const StatusIcon = statusConfig.icon;

                return (
                  <motion.div
                    key={payment.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                    className="p-4 hover:bg-[#1A2332]/30 transition-colors"
                  >
                    <div className="flex items-center gap-4">
                      {/* Status Icon */}
                      <div className={`w-10 h-10 rounded-lg ${statusConfig.bg} border ${statusConfig.border} flex items-center justify-center shrink-0`}>
                        <StatusIcon className={`w-5 h-5 ${statusConfig.text}`} />
                      </div>

                      {/* Payment Details */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-0.5">
                          <h4 className="text-sm font-medium text-white truncate">
                            {payment.planName ?? payment.subscription?.plan?.name ?? 'Subscription'}
                            <span className="text-(--color-muted-gray) font-normal"> • </span>
                            <span className="text-(--color-muted-gray) font-normal text-xs">
                              {(payment.billingCycle ?? payment.subscription?.billingCycle) === 'YEARLY' ? 'Annual' : 'Monthly'}
                            </span>
                          </h4>
                        </div>
                        <div className="flex items-center gap-3 text-xs text-(--color-muted-gray)">
                          <span className="flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            {formatDate(payment.paidAt || payment.createdAt)}
                          </span>
                          <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {formatTime(payment.paidAt || payment.createdAt)}
                          </span>
                          <span className="px-1.5 py-0.5 bg-[#1A2332] rounded text-[10px]">
                            {getPaymentTypeLabel(payment.type)}
                          </span>
                        </div>
                      </div>

                      {/* Amount */}
                      <div className="text-right shrink-0">
                        <p className="text-sm font-semibold text-white font-mono">
                          {formatCurrency(payment.amount, payment.currency)}
                        </p>
                        {payment.setupFee && payment.setupFee > 0 && (
                          <p className="text-[10px] text-(--color-muted-gray)">
                            Incl. setup: {formatCurrency(payment.setupFee, payment.currency)}
                          </p>
                        )}
                      </div>

                      {/* Status Badge */}
                      <div className="shrink-0">
                        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-medium ${statusConfig.bg} ${statusConfig.text}`}>
                          <StatusIcon className="w-3 h-3" />
                          {statusConfig.label}
                        </span>
                      </div>

                      {/* Action */}
                      <button
                        onClick={() => handleViewInvoice(payment.id)}
                        disabled={loadingInvoice === payment.id}
                        className="shrink-0 p-2 text-gray-400 hover:text-orange-400 hover:bg-orange-500/10 rounded-lg transition-colors disabled:opacity-50"
                        title="View Invoice"
                      >
                        {loadingInvoice === payment.id ? (
                          <RefreshCw className="w-4 h-4 animate-spin" />
                        ) : (
                          <ArrowRight className="w-4 h-4" />
                        )}
                      </button>
                    </div>
                  </motion.div>
                );
              })}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="px-4 py-3 border-t border-gray-800 bg-[#0D1117]/50">
                <Pagination
                  currentPage={page}
                  totalPages={totalPages}
                  totalItems={totalItems}
                  pageSize={pageSize}
                  onPageChange={setPage}
                  onPageSizeChange={(size) => {
                    setPageSize(size);
                    setPage(1);
                  }}
                />
              </div>
            )}
          </>
        )}
      </div>

      {/* Invoice Modal */}
      <Modal
        isOpen={!!selectedInvoice}
        onClose={() => setSelectedInvoice(null)}
        title="Invoice"
        size="lg"
        contentClassName="p-6"
      >
        {selectedInvoice && (
          <InvoicePreview
            invoice={selectedInvoice}
            formatCurrency={formatCurrency}
            formatDate={formatDate}
          />
        )}
      </Modal>
    </div>
  );
};

export default PaymentHistoryPage;
